import { BookOpenCheckIcon, CalendarCheckIcon, FileTextIcon, FolderOpenIcon, ListChecksIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type BonusGroup = {
  heading: string;
  items: string[];
};

export type Bonus = {
  number: string;
  title: string;
  description: string;
  items?: string[];
  groups?: BonusGroup[];
  cycle?: string[];
  benefit: string;
  value: string;
  icon: LucideIcon;
  wide?: boolean;
};

export const bonuses: Bonus[] = [
  {
    number: '01',
    title: '150 Surgical Instrumentation Teaching Activities & Challenges',
    description:
      '150 ready-to-use activity ideas covering surgical instrumentation fundamentals, instrument classification and identification, aseptic techniques and infection prevention, surgical anatomy and terminology, sterilization and instrument processing, perioperative workflow, operating room management, patient safety and error recognition, professional practice, critical thinking, technical communication, and integrated surgical instrumentation situations. Organized by difficulty level and topic.',
    benefit: 'Materials already designed for your classroom: saves hours every quarter.',
    value: 'USD 14',
    icon: FolderOpenIcon,
  },
  {
    number: '02',
    title: 'Surgical Instrumentation Teacher Assessment Kit',
    description:
      'Comprehensive assessment resources designed to evaluate student learning throughout a surgical instrumentation course. Includes:',
    items: [
      'Foundational level assessments',
      'Intermediate level assessments',
      'Advanced level assessments',
      'Instrument classification and function assessments',
      'Asepsis and infection prevention assessments',
      'Sterilization and instrument-processing assessments',
      'Surgical anatomy and medical terminology assessments',
      'Perioperative workflow and patient safety assessments',
      'Professional responsibility and ethical reasoning assessments',
      'Short-answer questions',
      'Analytical assessment activities',
      'Answer guidance and explanations',
      'Assessment criteria and feedback recommendations',
      'Teacher guidance',
    ],
    benefit: 'Never build an exam from scratch under pressure again.',
    value: 'USD 14',
    icon: ListChecksIcon,
  },
  {
    number: '03',
    title: '100 Surgical Instrumentation Case Studies & Problem-Solving Scenarios',
    description:
      'A collection of 100 developed surgical instrumentation cases involving instrument selection and identification, aseptic technique and contamination scenarios, surgical preparation and workflow situations, instrument processing and sterilization cases, operating room communication scenarios, patient safety and risk-recognition situations, professional responsibility and ethical dilemmas, and integrated surgical instrumentation challenges.',
    benefit:
      'Students analyze facts, separate known information from assumptions, identify missing evidence, and develop justified technical solutions.',
    value: 'USD 14',
    icon: FileTextIcon,
  },
  {
    number: '04',
    title: 'Surgical Instrumentation Teacher Dictionary: 200 Essential Concepts Explained',
    description:
      'Comprehensive reference handbook covering 200 essential surgical instrumentation concepts designed to help teachers explain vocabulary and concepts clearly. Includes terminology related to:',
    items: [
      'Surgical Instrumentation Fundamentals',
      'Instrument Classification',
      'Surgical Instruments by Type',
      'Anatomical and Medical Terminology',
      'Operating Room Terminology',
      'Asepsis and Infection Prevention Concepts',
      'Sterilization and Instrument-Processing Vocabulary',
      'Perioperative Workflow Terminology',
      'Patient Safety and Professional Practice Concepts',
      'Specialty-Specific Instrumentation',
      'Quality and Performance Concepts',
      'Equipment and Safety',
      'Professional Communication',
      'Healthcare Systems and Organization',
    ],
    benefit: 'Project it in class and your students instantly understand complex surgical instrumentation concepts.',
    value: 'USD 14',
    icon: BookOpenCheckIcon,
  },
  {
    number: '05',
    title: 'Surgical Instrumentation Teacher Lesson Planning Guide',
    description:
      'A complete planning resource designed to help educators turn technical material into organized lessons. Includes 50 Ready-to-Use Lesson Structures.',
    groups: [
      {
        heading: 'Organized into six teaching blocks',
        items: [
          'BLOCK 1 — Fundamentals & Operating Room Practice',
          'BLOCK 2 — Instrument Classification & Identification',
          'BLOCK 3 — Asepsis, Sterilization & Safety',
          'BLOCK 4 — Perioperative Workflow & Team Coordination',
          'BLOCK 5 — Specialized Instrumentation & Surgical Disciplines',
          'BLOCK 6 — Professional Practice & Patient Safety',
        ],
      },
      {
        heading: 'Each lesson structure includes',
        items: [
          'Learning Objectives',
          'Key Concepts',
          'Lesson Opening',
          'Core Explanation',
          'Guided Classroom Task',
          'Student Questions',
          'Understanding Check',
          'Common Misconceptions',
          'Lesson Connections',
        ],
      },
    ],
    cycle: ['Plan', 'Introduce', 'Explain', 'Connect', 'Apply', 'Question', 'Assess', 'Review', 'Integrate'],
    benefit: 'The guide follows a structured teaching cycle so every lesson is organized from start to finish.',
    value: 'USD 14',
    icon: CalendarCheckIcon,
    wide: true,
  },
];
